import { useState } from 'react'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { Group } from '@visx/group'
import { Legend } from '@visx/legend'
import { ParentSize } from '@visx/responsive'
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale'
import { BarGroup } from '@visx/shape'
import { useTooltip, useTooltipInPortal } from '@visx/tooltip'
import { localPoint } from '@visx/event'

export default function StrategyResultsGraphEntries({ data }) {
    const [view, setView] = useState('hourly')
    const { showTooltip, hideTooltip, tooltipLeft, tooltipTop, tooltipOpen, tooltipData } = useTooltip()
    const { containerRef, TooltipInPortal } = useTooltipInPortal({ detectBounds: true, scroll: true })

    const viewData = Object.keys(data[view]).sort((a, b) => a - b).map(key => {
        let keyLabel = parseInt(key)

        if (view === 'weekdays') {
            keyLabel = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][keyLabel - 1]
        } else if (view === 'months') {
            keyLabel = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][keyLabel - 1]
        }

        return {
            key: keyLabel,
            entries: data[view][key].entries
        }
    })

    const margin = { top: 40, right: 20, bottom: 50, left: 60 }

    const xAccessor = d => d.key
    const keys = ['entries']

    const handleMouseOver = (event, bar) => {
        const coords = localPoint(event.target.ownerSVGElement, event)
        showTooltip({
            tooltipLeft: coords.x,
            tooltipTop: coords.y,
            tooltipData: bar.value
        })
    }

    return (
        <ParentSize>
            {({ width }) => {

                const height = 400

                const xScale = scaleBand({
                    domain: viewData.map(xAccessor),
                    padding: 0.2,
                    range: [0, width - margin.left - margin.right]
                })

                const yScale = scaleLinear({
                    domain: [
                        0,
                        Math.max(
                            ...viewData.flatMap(d => keys.map(key => d[key]))
                        )
                    ],
                    nice: true,
                    range: [height - margin.top - margin.bottom, 0]
                })

                const colorScale = scaleOrdinal({
                    domain: keys,
                    range: ['var(--graph-bar-blue)']
                })

                let numTicks = 10
                if (view === 'weekdays') {
                    numTicks = 7
                } else if (view === 'months') {
                    numTicks = width > 350 ? 12 : 6
                } else if (view === 'hourly') {
                    numTicks = width > 650 ? 24 : 12
                }

                return (
                    <div>
                        <div className="flex items-center justify-between my-2 mx-4 flex-col-reverse max-[667px]:gap-3 md:flex-row">
                            <div className='flex items-center gap-2'>
                                {view === 'hourly' && (
                                    <>
                                        <div className="flex items-center gap-1 text-sm"><span className="bg-gray-400 dark:bg-gray-600 w-3 h-3 relative" style={{ backgroundColor: 'var(--graph-bar-teal)' }}></span><span>Asia</span></div>
                                        <div className="flex items-center gap-1 text-sm"><span className="bg-gray-400 dark:bg-gray-600 w-3 h-3 relative" style={{ backgroundColor: 'var(--graph-bar-violet)' }}></span><span>Europe</span></div>
                                        <div className="flex items-center gap-1 text-sm"><span className="bg-gray-400 dark:bg-gray-600 w-3 h-3 relative" style={{ backgroundColor: 'var(--graph-bar-blue)' }}></span><span>USA</span></div>
                                    </>
                                )}

                            </div>
                            <div className="flex items-center gap-2 text-sm">
                                <span>Sort By:</span>
                                <button className={`${view === 'hourly' ? 'text-blue-600 dark:text-blue-400' : 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400'}`} onClick={() => setView('hourly')}> Hourly </button>
                                <button className={`${view === 'weekdays' ? 'text-blue-600 dark:text-blue-400' : 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400'}`} onClick={() => setView('weekdays')}> Weekdays </button>
                                <button className={`${view === 'months' ? 'text-blue-600 dark:text-blue-400' : 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400'}`} onClick={() => setView('months')}> Months </button>
                            </div>
                        </div>
                        <svg ref={containerRef} width={width} height={height}>
                            <Group left={margin.left} top={margin.top}>
                                <BarGroup
                                    data={viewData}
                                    keys={keys}
                                    height={yScale(0)}
                                    x0={xAccessor}
                                    x0Scale={xScale}
                                    x1Scale={scaleBand({
                                        domain: keys,
                                        padding: 0.1,
                                        range: [0, xScale.bandwidth()]
                                    })}
                                    yScale={yScale}
                                    color={colorScale}
                                >
                                    {barGroups =>
                                        barGroups.map(barGroup => {
                                            return (
                                                <Group key={`bar-group-${barGroup.index}`} left={barGroup.x0}>
                                                    {barGroup.bars.map(bar => {
                                                        let fill = bar.color
                                                        if (view === 'hourly') {
                                                            if (barGroup.index < 8) {
                                                                fill = 'var(--graph-bar-teal)'
                                                            } else if (barGroup.index < 15) {
                                                                fill = 'var(--graph-bar-violet)'
                                                            }
                                                        }
                                                        return (
                                                            <rect
                                                                key={`bar-group-bar-${bar.index}-${bar.key}`}
                                                                x={bar.x}
                                                                y={bar.y}
                                                                width={bar.width}
                                                                height={bar.height}
                                                                fill={fill}
                                                                onMouseOver={e => handleMouseOver(e, bar)}
                                                                onMouseOut={hideTooltip}
                                                            />
                                                        )
                                                    })}
                                                </Group>
                                            )
                                        })
                                    }
                                </BarGroup>

                                <AxisLeft
                                    scale={yScale}
                                    tickFormat={value => value}
                                    label="Entries"
                                    stroke="#000"
                                    tickStroke="#000"
                                    tickLabelProps={() => ({
                                        fill: 'var(--graph-label)',
                                        fontSize: 11,
                                        textAnchor: 'end',
                                        dx: '-0.25em',
                                        dy: '0.25em'
                                    })}
                                />

                                <AxisBottom
                                    top={height - margin.top - margin.bottom}
                                    scale={xScale}
                                    label={view === 'hourly' ? 'Hour' : view === 'weekdays' ? 'Weekday' : 'Month'}
                                    stroke="#000"
                                    tickStroke="#000"
                                    numTicks={numTicks}
                                    tickLabelProps={() => ({
                                        fill: 'var(--graph-label)',
                                        fontSize: 11,
                                        textAnchor: 'middle'
                                    })}
                                />
                            </Group>

                            <Legend
                                top={10}
                                left={margin.left}
                                scale={colorScale}
                                direction="row"
                                shape="circle"
                                labelMargin="0 15px 0 0"
                                labelAlign="start"
                            />

                        </svg>
                        {tooltipOpen && (
                            <TooltipInPortal key={Math.random()} left={tooltipLeft} top={tooltipTop}> {tooltipData} </TooltipInPortal>
                        )}
                    </div>
                )
            }}
        </ParentSize>
    )
}


